import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { put } from '@nineeng/request';
import { Spinner } from '@repo/components';
import { EventEmitter } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyEvent, getMyCompanies } from 'src/api/company';
import GroupsTab from 'src/parts/tabs/GroupsTab';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/events/$eventId/groups')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, eventId } = Route.useParams();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['company-event', groupId, eventId],
		queryFn: () => getCompanyEvent(groupId, eventId),
		enabled: !userLoading,
	});

	const { data: groups } = useQuery({
		queryKey: ['company-groups'],
		queryFn: () => getMyCompanies(),
		enabled: !userLoading,
	});

	useBreadcrumbLevel(3, 'Groups');

	if (userLoading || eventLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !event) return null;

	const isEventAdmin = (groups || []).find((g) => event.groupIds?.includes(g.id));
	const canUpdateGroups = !!isEventAdmin;

	const handleDetailsUpdate = async (updatedEventData: any) => {
		await put(`/api/company-portal/groups/${groupId}/events/${eventId}`, updatedEventData)
			.then(() => EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' }))
			.catch(() => EventEmitter.emit('toast', { type: 'error', message: 'Error' }));
		queryClient.invalidateQueries({ queryKey: ['company-event', groupId, eventId] });
	};

	return (
		<GroupsTab event={event} onSave={handleDetailsUpdate} groups={event.flatGroups || []} readOnly={!canUpdateGroups} />
	);
}
